import { Link } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { GameLogo } from "@/components/GameLogo";
import { CountdownTicker } from "@/components/CountdownTicker";
import { NumberRow } from "@/components/NumberPill";
import { formatJackpot, formatDate } from "@/lib/format";
import { ExternalLink, ChevronRight } from "lucide-react";

interface JackpotCardProps {
  game: any;
  jackpotAmount: number;
  cashValue?: number | null;
  nextDrawDatetime?: string | null;
  allStates: Array<{ abbreviation: string; official_lottery_url?: string | null }>;
  isMultistate: boolean;
  gameLink: string;
  latestResult?: any;
  priority?: boolean;
}

const KNOWN_URLS: Record<string, string> = {
  powerball: "https://www.powerball.com",
  "mega-millions": "https://www.megamillions.com",
};

export function JackpotCard({
  game,
  jackpotAmount,
  cashValue,
  nextDrawDatetime,
  allStates,
  isMultistate,
  gameLink,
  latestResult,
  priority = false,
}: JackpotCardProps) {
  const playNowUrl =
    KNOWN_URLS[game?.slug] ||
    allStates.find((s) => s.official_lottery_url)?.official_lottery_url ||
    null;

  return (
    <Card className="hover:shadow-lg transition-shadow h-full flex flex-col">
      <CardContent className="p-5 flex flex-col items-center text-center gap-3 flex-1">
        <div>
          <h3 className="font-sans font-bold text-base leading-tight">{game?.name}</h3>
          <div className="flex flex-wrap justify-center gap-1 mt-1">
            {isMultistate ? (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                Multi-State
              </Badge>
            ) : null}
            {allStates.map((s) => (
              <Badge key={s.abbreviation} variant="outline" className="text-[10px] px-1.5 py-0">
                {s.abbreviation}
              </Badge>
            ))}
          </div>
        </div>

        <GameLogo
          gameSlug={game?.slug || ""}
          gameName={game?.name || ""}
          logoUrl={game?.logo_url}
          size="xl"
          className="h-40 w-40 text-2xl"
          priority={priority}
        />

        <div>
          <p className="jackpot-amount">{formatJackpot(jackpotAmount)}</p>
          {cashValue && (
            <p className="text-sm text-muted-foreground">
              Est. cash option: {formatJackpot(cashValue)}
            </p>
          )}
        </div>

        <div className="min-h-[76px] w-full">
          {latestResult && (
            <div>
              <p className="text-xs text-muted-foreground mb-1.5">
                Last draw — {formatDate(latestResult.draw_date)}
              </p>
              <NumberRow
                numbers={latestResult.winning_numbers || []}
                bonusNumbers={latestResult.bonus_numbers || []}
                multiplier={latestResult.multiplier}
                size="md"
              />
            </div>
          )}
        </div>

        {nextDrawDatetime && <CountdownTicker targetDate={nextDrawDatetime} variant="card" />}

        <div className="flex flex-col sm:flex-row gap-2 mt-auto pt-2 w-full">
          {playNowUrl && (
            <Button
              size="sm"
              className="flex-1 min-w-0 w-full h-auto p-2.5 text-sm sm:text-xs"
              asChild
            >
              <a href={playNowUrl} target="_blank" rel="noopener noreferrer">
                Play Now <ExternalLink className="ml-1 h-4 w-4 sm:h-3.5 sm:w-3.5" />
              </a>
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            className="flex-1 min-w-0 w-full h-auto p-2.5 text-sm sm:text-xs hover:border-accent hover:bg-accent hover:text-white"
            asChild
          >
            <a href={gameLink} className="truncate">
              <span className="sm:hidden">{game?.name} details</span>
              <span className="hidden sm:inline truncate">{game?.name} Details</span>
              <ChevronRight className="ml-1 h-4 w-4 sm:h-3.5 sm:w-3.5 shrink-0" />
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
